import { create } from "zustand";
import * as Types from "@/models/types";
import { MOBILE_MAX_WIDTH } from "@/constants";
import {
  CustomerInfo,
  PurchasesPackage,
} from "@revenuecat/purchases-capacitor";
import { LiveRecordingStatus } from "./useLiveRecorderIOS";

type State = {
  revenueCat?: {
    packages: PurchasesPackage[];
    customerInfo: CustomerInfo;
  };
  liveRecording?: {
    noteId: string;
    status?: LiveRecordingStatus;
  };
  alertWidgetAIContentTemplates?: {
    onDeleteCallback?: () => void;
  };
  alertWidgetAIContentEditWithAI?: {
    noteId?: string;
    title?: string;
    callback?: () => void;
    userTemplateId?: Types.User["listTemplates"][number]["id"];
  };
  noteScreenData?: {
    upload_started?: {
      file: File;
      language?: string;
    };
  };
  selectedFolder?: {
    folder: Types.Folder;
    notes: Types.Note[];
  };
  alertExportTranscriptOptions?: {
    hasSpeakers: boolean;
    onExport: (options: {
      exportType: "docx" | "pdf" | "txt" | "srt";
      withTimestamps: boolean;
      withSpeakers: boolean;
      withMonologue: boolean;
    }) => void;
  };
  alertTranscriptRenameSpeakers?: {
    noteId: string;
    title: string;
    onSpeakerEdit: (speaker: string) => void;
  };
  device: {
    isMobile: boolean;
    width: number;
    height: number;
    safeArea: {
      top: number;
      bottom: number;
    };
  };
  alertSelect?: {
    title?: string;
    buttonText?: string;
    defaultOption?: number;
    options: string[];
    onSelect?: (index: number) => void;
    onSelectAsync?: (index: number) => Promise<void>;
  };
  useUploadParams?: {
    fileType: string;
    key: string;
  };
  paywallVisible?: boolean;
  alertPromptSignIn?: {
    title: string;
    subtitle: string;
    placeholder: string;
    shouldPersistBackdropClick?: boolean;
    multiLine?: boolean;
    loading?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    defaultValue?: string;
    button: {
      text: string;
      onPress: (text: string) => void;
      destructive?: boolean;
    };
    onGoogleSignIn?: () => void;
    onAppleSignIn?: () => void;
  };
  alertModal?: {
    title: string;
    loading?: boolean;
    subtitle: string;
    shouldPersistBackdropClick?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    onDismiss?: () => void;
    onLeftButtonPress?: () => void;
    button?: { text: string; onPress: () => void; destructive?: boolean };
    cancelText?: string;
  };
  alertPrompt?: {
    title: string;
    subtitle?: string;
    placeholder: string;
    shouldPersistBackdropClick?: boolean;
    multiLine?: boolean;
    loading?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    defaultValue?: string;
    button: {
      text: string;
      onPress: (text: string) => void;
      destructive?: boolean;
    };
  };
  alertWidgetStartUpload?: {
    file: File;
    language?: string;
    onSuccess?: (params: { downloadUrl: string; language: string }) => void;
  };
  alertWidgetChat?: {
    note?: Types.Note;
    folder?: Types.Folder;
  };
  alertWidgetSearch?: {
    noteId?: string;
    folderId?: string;
    placeholder?: string;
  };
  alertWidget?:
    | Types.Note["type"]
    | "live_transcription"
    | "manageFolders"
    | "createAIContent"
    | "createNote";
};

type Actions = {
  setDevice: (payload: State["device"]) => void;
  setSelectedFolder: (payload: State["selectedFolder"]) => void;
  setAlertExportTranscriptOptions: (
    payload: State["alertExportTranscriptOptions"]
  ) => void;
  setAlertModal: (payload: State["alertModal"]) => void;
  setLiveRecording: (payload: State["liveRecording"]) => void;
  setAlertWidgetChat: (payload: State["alertWidgetChat"]) => void;
  setAlertWidgetAIContentTemplates: (
    payload: State["alertWidgetAIContentTemplates"],
    onDeleteCallback?: () => void
  ) => void;
  setAlertWidgetAIContentEditWithAI: (
    payload: State["alertWidgetAIContentEditWithAI"]
  ) => void;
  setAlertWidgetSearch: (payload: State["alertWidgetSearch"]) => void;
  setAlertTranscriptRenameSpeakers: (
    payload: State["alertTranscriptRenameSpeakers"]
  ) => void;
  setAlertSelect: (payload: State["alertSelect"]) => void;
  setNoteScreenData: (payload: State["noteScreenData"]) => void;
  setAlertWidgetStartUpload: (payload: State["alertWidgetStartUpload"]) => void;
  setUseUploadParams: (payload: State["useUploadParams"]) => void;
  setAlertPromptSignIn: (payload: State["alertPromptSignIn"]) => void;
  setAlertPrompt: (payload: State["alertPrompt"]) => void;
  setAlertWidget: (payload: State["alertWidget"]) => void;
  setPaywallVisible: (payload: State["paywallVisible"]) => void;
  setRevenueCat: (payload: State["revenueCat"]) => void;
  reset: () => void;
};

// define the initial state
const initialState: State = {
  device: {
    isMobile: window.innerWidth < MOBILE_MAX_WIDTH,
    width: window.innerWidth,
    height: window.innerHeight,
    safeArea: {
      top: 0,
      bottom: 0,
    },
  },
};

export const useUISlice = create<State & Actions>()((set, get) => ({
  ...initialState,
  setDevice: (payload) => {
    set({ device: payload });
  },
  setAlertWidgetChat: (payload) => {
    set({ alertWidgetChat: payload });
  },
  setLiveRecording: (payload) => {
    set({ liveRecording: payload });
  },
  setAlertWidgetAIContentTemplates: (payload) => {
    set({ alertWidgetAIContentTemplates: payload });
  },
  setAlertWidgetAIContentEditWithAI: (payload) => {
    set({ alertWidgetAIContentEditWithAI: payload });
  },
  setAlertWidgetSearch: (payload) => {
    set({ alertWidgetSearch: payload });
  },
  setRevenueCat: (payload) => {
    set({ revenueCat: payload });
  },
  setAlertTranscriptRenameSpeakers: (payload) => {
    set({ alertTranscriptRenameSpeakers: payload });
  },
  setSelectedFolder: (payload) => {
    set({ selectedFolder: payload });
  },
  setAlertExportTranscriptOptions: (payload) => {
    set({ alertExportTranscriptOptions: payload });
  },
  setAlertSelect: (payload) => {
    set({ alertSelect: payload });
  },
  setNoteScreenData: (payload) => {
    set({ noteScreenData: payload });
  },
  setAlertWidgetStartUpload: (payload) => {
    set({ alertWidgetStartUpload: payload });
  },
  setUseUploadParams: (payload) => {
    set({ useUploadParams: payload });
  },
  setAlertPromptSignIn: (payload) => {
    set({ alertPromptSignIn: payload });
  },
  setAlertModal: (payload) => {
    set({ alertModal: payload });
  },
  setAlertPrompt: (payload) => {
    set({ alertPrompt: payload });
  },
  setAlertWidget: (payload) => {
    set({ alertWidget: payload });
  },
  setPaywallVisible: (payload) => {
    set({ paywallVisible: payload });
  },
  reset: () => {
    set({ ...initialState });
  },
}));
